/**
 * EmptyState — single primitive for blank list surfaces.
 * Always pair with a primary CTA so the member has an obvious next step.
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaOnClick?: () => void;
  secondaryLabel?: string;
  secondaryHref?: string;
}

const EmptyState = ({
  icon: Icon,
  title,
  description,
  ctaLabel,
  ctaHref,
  ctaOnClick,
  secondaryLabel,
  secondaryHref,
}: EmptyStateProps) => {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
      {Icon && (
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground">
          <Icon className="h-5 w-5" aria-hidden />
        </div>
      )}
      <h3 className="text-base font-semibold">{title}</h3>
      {description && (
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {(ctaLabel || secondaryLabel) && (
        <div className="mt-6 flex items-center justify-center gap-2">
          {ctaLabel && ctaHref && (
            <Button asChild size="sm">
              <Link to={ctaHref}>{ctaLabel}</Link>
            </Button>
          )}
          {ctaLabel && !ctaHref && ctaOnClick && (
            <Button size="sm" onClick={ctaOnClick}>
              {ctaLabel}
            </Button>
          )}
          {secondaryLabel && secondaryHref && (
            <Button asChild size="sm" variant="ghost">
              <Link to={secondaryHref}>{secondaryLabel}</Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
